import React from 'react';
import { Link } from 'react-router-dom';
import { Issue, Status } from '../types';

interface IssueMember {
  user_id: string;
  full_name: string | null;
  email: string;
  role: string;
}

interface IssueCardProps {
  issue: Issue;
  canUpdateStatus?: boolean;
  onStatusChange?: (issueId: string, status: Status) => void;
  updatingStatus?: boolean;
  creatorName?: string;
  canAssign?: boolean;
  members?: IssueMember[];
  memberMap?: Record<string, string>;
  onAssign?: (issueId: string, field: 'assigned_developer_id' | 'assigned_tester_id', userId: string | null) => void;
}

const priorityConfig: Record<string, { label: string; className: string }> = {
  High:   { label: 'High',   className: 'priority-high' },
  Medium: { label: 'Medium', className: 'priority-medium' },
  Low:    { label: 'Low',    className: 'priority-low' },
};

const statusConfig: Record<string, { label: string; className: string; icon: string }> = {
  'Open':        { label: 'Open',        className: 'status-open',        icon: '○' },
  'In Progress': { label: 'In Progress', className: 'status-in-progress', icon: '◑' },
  'Closed':      { label: 'Closed',      className: 'status-closed',      icon: '●' },
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function avatarBg(seed: string) {
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#f97316'];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function AssigneeChip({ name }: { name: string }) {
  return (
    <span className="assignee-chip">
      <span className="assignee-chip-avatar" style={{ background: avatarBg(name) }}>
        {name.charAt(0).toUpperCase()}
      </span>
      <span className="assignee-chip-name">{name}</span>
    </span>
  );
}

export default function IssueCard({
  issue,
  canUpdateStatus = false,
  onStatusChange,
  updatingStatus = false,
  creatorName,
  canAssign = false,
  members = [],
  memberMap = {},
  onAssign,
}: IssueCardProps) {
  const priority = priorityConfig[issue.priority] ?? priorityConfig.Medium;
  const status   = statusConfig[issue.status]    ?? statusConfig['Open'];

  const plainDescription = issue.description ? stripHtml(issue.description) : '';

  const devName    = issue.assigned_developer_id ? (memberMap[issue.assigned_developer_id] ?? null) : null;
  const testerName = issue.assigned_tester_id    ? (memberMap[issue.assigned_tester_id]    ?? null) : null;

  const showAssignees = canAssign || devName || testerName;

  const stopNav = (e: React.MouseEvent | React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Link to={`/issue/${issue.id}`} className="issue-card">
      <div className="issue-card-header">
        <h3 className="issue-card-title">{issue.title}</h3>
        <span className={`priority-badge ${priority.className}`}>{priority.label}</span>
      </div>

      {plainDescription && (
        <p className="issue-card-description">
          {plainDescription.length > 120 ? plainDescription.slice(0, 120) + '…' : plainDescription}
        </p>
      )}

      {/* Assignment row */}
      {showAssignees && (
        <div className="issue-card-assignees" onClick={stopNav}>
          {/* Developer */}
          <div className="assignee-field">
            <span className="assignee-field-label">Dev</span>
            {canAssign ? (
              <select
                className="assignee-select"
                value={issue.assigned_developer_id ?? ''}
                onClick={stopNav}
                onChange={(e) => {
                  stopNav(e);
                  onAssign?.(issue.id, 'assigned_developer_id', e.target.value || null);
                }}
              >
                <option value="">Unassigned</option>
                {members.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.full_name || m.email}
                  </option>
                ))}
              </select>
            ) : devName ? (
              <AssigneeChip name={devName} />
            ) : (
              <span className="assignee-empty">Unassigned</span>
            )}
          </div>

          {/* Tester / QA */}
          <div className="assignee-field">
            <span className="assignee-field-label">QA</span>
            {canAssign ? (
              <select
                className="assignee-select"
                value={issue.assigned_tester_id ?? ''}
                onClick={stopNav}
                onChange={(e) => {
                  stopNav(e);
                  onAssign?.(issue.id, 'assigned_tester_id', e.target.value || null);
                }}
              >
                <option value="">Unassigned</option>
                {members.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.full_name || m.email}
                  </option>
                ))}
              </select>
            ) : testerName ? (
              <AssigneeChip name={testerName} />
            ) : null}
          </div>
        </div>
      )}

      <div className="issue-card-footer">
        {canUpdateStatus ? (
          <select
            className="issue-status-select"
            value={issue.status}
            disabled={updatingStatus}
            onClick={(e) => e.preventDefault()}
            onChange={(e) => {
              e.preventDefault();
              onStatusChange?.(issue.id, e.target.value as Status);
            }}
          >
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Closed">Closed</option>
          </select>
        ) : (
          <span className={`status-badge ${status.className}`}>
            {status.icon} {status.label}
          </span>
        )}

        <div className="issue-card-meta">
          {creatorName && (
            <span className="meta-item meta-creator" title={`Created by ${creatorName}`}>
              <span className="issue-creator-avatar" style={{ background: avatarBg(creatorName) }}>
                {creatorName.charAt(0).toUpperCase()}
              </span>
              <span className="issue-creator-name">{creatorName}</span>
            </span>
          )}
          {issue.images && issue.images.length > 0 && (
            <span className="meta-item" title={`${issue.images.length} image(s)`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              {issue.images.length}
            </span>
          )}
          <span className="meta-date">{formatDate(issue.created_at)}</span>
        </div>
      </div>
    </Link>
  );
}
